import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Star, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  Plus,
  Eye,
  TrendingUp,
  Award,
  Users,
  FileText,
  Calendar,
  DollarSign
} from 'lucide-react';

const MultiVerifierSystem = ({ projectId }) => {
  const [project, setProject] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [credibilityScore, setCredibilityScore] = useState(null);
  const [availableVerifiers, setAvailableVerifiers] = useState([]);
  const [showAddVerifier, setShowAddVerifier] = useState(false);
  const [loading, setLoading] = useState(true);

  // Mock project data with multiple verifications
  const mockProject = {
    id: projectId || "PRJ-123",
    title: "Amazon Rainforest Conservation",
    projectType: "Forest Conservation",
    location: "Brazil",
    totalCredits: 50000,
    developer: "EcoForest Initiative",
    registrationDate: "2023-06-15",
    status: "active"
  };

  const mockVerifications = [
    {
      id: "VER-001",
      verifierId: "V-001",
      verifierName: "Carbon Integrity Ltd",
      verifierType: "platform", // platform or third-party
      status: "completed",
      standard: "Gold Standard",
      creditsVerified: 50000,
      verificationDate: "2023-08-15",
      expiryDate: "2024-08-15",
      score: 9.2,
      confidence: "High",
      methodology: "VM0007 REDD+ Methodology Framework",
      registryId: "GS-12345-BR-2023",
      cost: 2500,
      timeline: 45, // days
      reportUrl: "#",
      certificateUrl: "#",
      
      // Credibility factors
      credibilityFactors: {
        verifierRating: 4.9,
        verifierExperience: 15,
        verifierProjects: 220,
        standardRigor: 9.5,
        methodologyRobustness: 9.0,
        documentationQuality: 9.3,
        independenceLevel: 10,
        technicalDepth: 8.8
      },
      
      // Review details
      reviewSummary: {
        strengths: [
          "Comprehensive field verification conducted",
          "Robust baseline methodology applied",
          "Strong community engagement documented",
          "Advanced remote sensing validation used"
        ],
        recommendations: [
          "Enhance monitoring frequency for biodiversity metrics",
          "Strengthen stakeholder communication protocols"
        ],
        riskAssessment: "Low risk of non-permanence"
      }
    },
    {
      id: "VER-002",
      verifierId: "V-002",
      verifierName: "SCS Global Services",
      verifierType: "third-party",
      status: "completed",
      standard: "VCS",
      creditsVerified: 48500,
      verificationDate: "2023-09-20",
      expiryDate: "2024-09-20",
      score: 8.7,
      confidence: "High",
      methodology: "VM0015 Methodology for Avoided Unplanned Deforestation",
      registryId: "VCS-98765-BR-2023",
      cost: 3200,
      timeline: 52,
      reportUrl: "#",
      certificateUrl: "#",
      
      credibilityFactors: {
        verifierRating: 4.8,
        verifierExperience: 20,
        verifierProjects: 450,
        standardRigor: 9.0,
        methodologyRobustness: 8.5,
        documentationQuality: 8.9,
        independenceLevel: 10,
        technicalDepth: 8.5
      },
      
      reviewSummary: {
        strengths: [
          "Independent third-party validation",
          "Extensive stakeholder consultation",
          "Rigorous carbon accounting verified",
          "Comprehensive risk analysis conducted"
        ],
        recommendations: [
          "Consider additional biodiversity co-benefits quantification",
          "Implement enhanced monitoring for social impacts"
        ],
        riskAssessment: "Low to moderate risk"
      }
    },
    {
      id: "VER-003",
      verifierId: "V-003",
      verifierName: "EcoVerify International",
      verifierType: "platform",
      status: "in_progress",
      standard: "Plan Vivo",
      creditsVerified: null,
      verificationDate: null,
      expiryDate: null,
      score: null,
      confidence: null,
      methodology: "Plan Vivo Standard for Community Payments for Ecosystem Services",
      registryId: "Pending",
      cost: 1800,
      timeline: 35,
      estimatedCompletion: "2024-03-15",
      reportUrl: null,
      certificateUrl: null,
      
      progress: {
        documentsReview: "completed",
        fieldVerification: "in_progress",
        stakeholderConsultation: "pending",
        reportDrafting: "pending",
        finalReview: "pending"
      }
    }
  ];

  const mockCredibilityScore = {
    overall: 8.95,
    grade: "A",
    confidence: "Very High",
    lastUpdated: "2024-01-20",
    
    components: {
      verification_count: {
        score: 9.2,
        weight: 25,
        description: "Multiple independent verifications completed"
      },
      verifier_quality: {
        score: 9.1,
        weight: 30,
        description: "High-quality, experienced verifiers with strong track records"
      },
      standard_rigor: {
        score: 9.25,
        weight: 20,
        description: "Verified under rigorous international standards"
      },
      methodology_robustness: {
        score: 8.75,
        weight: 15,
        description: "Robust methodologies with proven track records"
      },
      independence: {
        score: 10,
        weight: 10,
        description: "Strong independence with third-party and platform verifications"
      }
    },
    
    factors: {
      verification_count: 2, // completed verifications
      third_party_verifications: 1,
      platform_verifications: 1,
      standards_coverage: ["Gold Standard", "VCS", "Plan Vivo"],
      avg_verifier_rating: 4.85,
      total_verifier_experience: 35,
      methodology_diversity: 3,
      registry_coverage: 2
    },
    
    benchmarks: {
      industry_average: 6.2,
      top_quartile: 7.8,
      top_decile: 8.5
    },
    
    trend: {
      direction: "improving",
      change: "+0.3",
      period: "last 6 months"
    }
  };

  const mockAvailableVerifiers = [
    {
      id: "V-004",
      name: "Climate Focus Verification",
      type: "third-party",
      rating: 4.7,
      experience: 12,
      specialties: ["Forest Conservation", "REDD+"],
      standards: ["Gold Standard", "VCS", "CDM"],
      estimatedCost: 2800,
      estimatedTimeline: 40,
      availability: "Available"
    },
    {
      id: "V-005",
      name: "Green Standard Verification",
      type: "platform",
      rating: 4.6,
      experience: 8,
      specialties: ["Forest Conservation", "Blue Carbon"],
      standards: ["Plan Vivo", "Social Carbon"],
      estimatedCost: 2200,
      estimatedTimeline: 35,
      availability: "Available"
    }
  ];

  useEffect(() => {
    // Simulate API calls
    setTimeout(() => {
      setProject(mockProject);
      setVerifications(mockVerifications);
      setCredibilityScore(mockCredibilityScore);
      setAvailableVerifiers(mockAvailableVerifiers);
      setLoading(false);
    }, 1000);
  }, [projectId]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A':
        return 'text-green-600 bg-green-100';
      case 'B':
        return 'text-blue-600 bg-blue-100';
      case 'C':
        return 'text-yellow-600 bg-yellow-100';
      case 'D':
        return 'text-orange-600 bg-orange-100';
      case 'F':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getVerifierTypeIcon = (type) => {
    return type === 'third-party' ? 
      <Shield className="h-4 w-4 text-blue-600" /> : 
      <Award className="h-4 w-4 text-green-600" />;
  };

  const calculateOverallCredibility = (verifications) => {
    if (verifications.length === 0) return 0;
    
    const completedVerifications = verifications.filter(v => v.status === 'completed');
    if (completedVerifications.length === 0) return 0;
    
    const avgScore = completedVerifications.reduce((sum, v) => sum + v.score, 0) / completedVerifications.length;
    const countBonus = Math.min(completedVerifications.length * 0.5, 2); // Bonus for multiple verifications
    const diversityBonus = new Set(completedVerifications.map(v => v.standard)).size * 0.3;
    
    return Math.min(avgScore + countBonus + diversityBonus, 10);
  };

  const handleAddVerifier = async (verifierId) => {
    try {
      // Simulate API call
      console.log(`Adding verifier ${verifierId} to project ${project.id}`);
      
      const verifier = availableVerifiers.find(v => v.id === verifierId);
      const newVerification = {
        id: `VER-${Date.now()}`,
        verifierId: verifier.id,
        verifierName: verifier.name,
        verifierType: verifier.type,
        status: "pending",
        standard: verifier.standards[0],
        creditsVerified: null,
        cost: verifier.estimatedCost,
        timeline: verifier.estimatedTimeline,
        methodology: "To be determined",
        registryId: "Pending"
      };

      setVerifications(prev => [...prev, newVerification]);
      setShowAddVerifier(false);
      alert('Verifier added successfully! They will be contacted to begin verification.');
    } catch (error) {
      console.error('Error adding verifier:', error);
      alert('Error adding verifier. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Multi-Verifier System</h1>
        <p className="text-gray-600">{project.title} - Verification Overview</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Credibility Score */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-8 border">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full border-8 border-green-200 flex items-center justify-center bg-white">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{credibilityScore.overall}</div>
                      <div className={`text-lg font-semibold px-2 py-1 rounded ${getGradeColor(credibilityScore.grade)}`}>
                        {credibilityScore.grade}
                      </div>
                    </div>
                  </div>
                  <div className="absolute -top-2 -right-2">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Credibility Score</h2>
              <p className="text-gray-600 mb-4">
                Based on {verifications.filter(v => v.status === 'completed').length} completed verifications
              </p>
              
              <div className="flex justify-center items-center space-x-6 text-sm">
                <div className="text-center">
                  <p className="font-semibold text-gray-900">{credibilityScore.confidence}</p>
                  <p className="text-gray-600">Confidence</p>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-green-600">{credibilityScore.trend.change}</p>
                  <p className="text-gray-600">6 month trend</p>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-blue-600">Top 5%</p>
                  <p className="text-gray-600">Percentile</p>
                </div>
              </div>
            </div>

            {/* Score Components */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(credibilityScore.components).map(([key, component]) => (
                <div key={key} className="bg-white rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-900 capitalize">
                      {key.replace('_', ' ')}
                    </h4>
                    <span className="text-sm font-semibold text-gray-900">
                      {component.score}/10
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${(component.score / 10) * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600">{component.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Verifications List */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Verifications</h3>
                <button
                  onClick={() => setShowAddVerifier(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center text-sm font-medium"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Verifier
                </button>
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {verifications.map((verification) => (
                <div key={verification.id} className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <div className="flex items-center mr-4">
                          {getVerifierTypeIcon(verification.verifierType)}
                          <h4 className="ml-2 text-lg font-medium text-gray-900">
                            {verification.verifierName}
                          </h4>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(verification.status)}`}>
                          {verification.status.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-4">
                        <div>
                          <span className="font-medium">Standard:</span>
                          <p>{verification.standard}</p>
                        </div>
                        <div>
                          <span className="font-medium">Registry ID:</span>
                          <p>{verification.registryId}</p>
                        </div>
                        <div>
                          <span className="font-medium">Cost:</span>
                          <p>${verification.cost?.toLocaleString()}</p>
                        </div>
                        <div>
                          <span className="font-medium">Timeline:</span>
                          <p>{verification.timeline} days</p>
                        </div>
                      </div>

                      {verification.status === 'completed' && (
                        <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-4">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-medium text-green-900">Verification Results</h5>
                            <div className="flex items-center">
                              <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                              <span className="font-semibold text-green-900">{verification.score}/10</span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="font-medium text-green-900 mb-1">Credits Verified:</p>
                              <p className="text-green-800">{verification.creditsVerified?.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="font-medium text-green-900 mb-1">Verification Date:</p>
                              <p className="text-green-800">{new Date(verification.verificationDate).toLocaleDateString()}</p>
                            </div>
                            <div>
                              <p className="font-medium text-green-900 mb-1">Confidence Level:</p>
                              <p className="text-green-800">{verification.confidence}</p>
                            </div>
                            <div>
                              <p className="font-medium text-green-900 mb-1">Valid Until:</p>
                              <p className="text-green-800">{new Date(verification.expiryDate).toLocaleDateString()}</p>
                            </div>
                          </div>

                          {verification.reviewSummary && (
                            <div className="mt-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <p className="font-medium text-green-900 mb-2">Key Strengths:</p>
                                  <ul className="space-y-1">
                                    {verification.reviewSummary.strengths.slice(0, 2).map((strength, index) => (
                                      <li key={index} className="text-green-800 text-xs flex items-start">
                                        <CheckCircle className="h-3 w-3 text-green-600 mr-1 mt-0.5 flex-shrink-0" />
                                        {strength}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div>
                                  <p className="font-medium text-green-900 mb-2">Risk Assessment:</p>
                                  <p className="text-green-800 text-xs">{verification.reviewSummary.riskAssessment}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {verification.status === 'in_progress' && verification.progress && (
                        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
                          <h5 className="font-medium text-blue-900 mb-3">Verification Progress</h5>
                          <div className="space-y-2">
                            {Object.entries(verification.progress).map(([step, status]) => (
                              <div key={step} className="flex items-center justify-between">
                                <span className="text-sm text-blue-800 capitalize">
                                  {step.replace(/([A-Z])/g, ' $1').toLowerCase()}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                                  {status}
                                </span>
                              </div>
                            ))}
                          </div>
                          {verification.estimatedCompletion && (
                            <p className="text-sm text-blue-700 mt-3">
                              Estimated completion: {new Date(verification.estimatedCompletion).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <button className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-100">
                        <Eye className="h-4 w-4" />
                      </button>
                      {verification.reportUrl && (
                        <button className="text-gray-600 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100">
                          <FileText className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Project Summary */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Summary</h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Credits:</span>
                <span className="font-medium">{project.totalCredits?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Project Type:</span>
                <span className="font-medium">{project.projectType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Location:</span>
                <span className="font-medium">{project.location}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Developer:</span>
                <span className="font-medium">{project.developer}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Registration:</span>
                <span className="font-medium">{new Date(project.registrationDate).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Verification Stats */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Stats</h3>
            
            <div className="space-y-4">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">
                  {verifications.filter(v => v.status === 'completed').length}
                </p>
                <p className="text-sm text-gray-600">Completed Verifications</p>
              </div>
              
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {new Set(verifications.map(v => v.standard)).size}
                </p>
                <p className="text-sm text-gray-600">Standards Covered</p>
              </div>
              
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">
                  {verifications.filter(v => v.verifierType === 'third-party').length}
                </p>
                <p className="text-sm text-gray-600">Third-party Verifications</p>
              </div>
            </div>
          </div>

          {/* Benchmark Comparison */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Benchmark Comparison</h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Your Score</span>
                  <span className="font-medium">{credibilityScore.overall}/10</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${(credibilityScore.overall / 10) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Industry Average</span>
                  <span className="font-medium">{credibilityScore.benchmarks.industry_average}/10</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gray-400 h-2 rounded-full"
                    style={{ width: `${(credibilityScore.benchmarks.industry_average / 10) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Top 10%</span>
                  <span className="font-medium">{credibilityScore.benchmarks.top_decile}/10</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-400 h-2 rounded-full"
                    style={{ width: `${(credibilityScore.benchmarks.top_decile / 10) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
                Download Verification Reports
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
                Share Credibility Score
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
                Request Additional Verification
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
                View Detailed Analytics
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Verifier Modal */}
      {showAddVerifier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Add Additional Verifier</h2>
              <p className="text-gray-600">Select a verifier to add another verification to your project</p>
            </div>
            
            <div className="p-6 space-y-4">
              {availableVerifiers.map((verifier) => (
                <div key={verifier.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center mb-2">
                        {getVerifierTypeIcon(verifier.type)}
                        <h4 className="ml-2 font-medium text-gray-900">{verifier.name}</h4>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                          <span>{verifier.rating}</span>
                        </div>
                        <span>{verifier.experience} years experience</span>
                        <span className="capitalize">{verifier.type}</span>
                      </div>
                    </div>
                    <span className="text-green-600 text-sm font-medium">{verifier.availability}</span>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-sm text-gray-600 mb-1">Specialties:</p>
                    <div className="flex flex-wrap gap-1">
                      {verifier.specialties.map((specialty, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Estimated: </span>
                      ${verifier.estimatedCost.toLocaleString()} • {verifier.estimatedTimeline} days
                    </div>
                    <button
                      onClick={() => handleAddVerifier(verifier.id)}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                    >
                      Select Verifier
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowAddVerifier(false)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiVerifierSystem; 